import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import API_BASE_URL, { getEffectiveTenantHost } from '../config';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { getUserInfoRaw } from '../utils/tokenStorage';
import {
  isTransientHttpStatus,
  isTransientNetworkError,
  transientUnavailable,
  withTransientRetries,
} from '../utils/transientRequest';

/** Long enough for a post-restart MySQL handshake plus tenant views.
 *  Connection refused still fails in milliseconds. */
const FETCH_USER_ROLES_TIMEOUT_MS = 12000;
const ROLES_SESSION_KEY = 'safework.cachedUserRoles';

interface StoredUserRoles {
  userId: string;
  host: string;
  roles: string[];
}

let memoryCachedRoles: StoredUserRoles | null = null;

function cacheHost(): string {
  if (typeof window === 'undefined') return '';
  return getEffectiveTenantHost();
}

function readSessionUserId(): string | null {
  const raw = getUserInfoRaw();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { userId?: unknown };
    return typeof parsed.userId === 'string' && parsed.userId.length > 0 ? parsed.userId : null;
  } catch {
    return null;
  }
}

function normalizeRoles(roles: readonly string[]): string[] {
  const names = roles.filter((role) => role.length > 0);
  return names.includes('user') ? [...names] : [...names, 'user'];
}

const CLIENT_ROLE_HINTS = new Set(['admin', 'System Admin', 'Tenant Admin', 'manager']);

/**
 * Privileged roles already stored on `oidc_user_info` (dev auto-login, or a
 * previous profile write). An empty list from AuthCallback is not a hint:
 * the server has not confirmed access yet. A hint is display-only until
 * getUserRoles returns.
 */
export function readClientRoleHint(userId: string | null | undefined): string[] | null {
  if (!userId) return null;
  const raw = getUserInfoRaw();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { userId?: unknown; userRoles?: unknown };
    if (parsed.userId !== userId || !Array.isArray(parsed.userRoles)) return null;
    const roles = parsed.userRoles.filter(
      (role): role is string => typeof role === 'string' && role.length > 0
    );
    if (!roles.some((role) => CLIENT_ROLE_HINTS.has(role))) return null;
    return normalizeRoles(roles);
  } catch {
    return null;
  }
}

/** Session cache first, then a privileged client hint. Otherwise not ready. */
export function knownRolesForSession(): { roles: string[]; ready: boolean } {
  const userId = readSessionUserId();
  const cached = readCachedUserRoles(userId);
  if (cached) return { roles: cached, ready: true };
  const hinted = readClientRoleHint(userId);
  if (hinted) return { roles: hinted, ready: true };
  return { roles: ['user'], ready: false };
}

/** Last roles fetched for this user on this host, from memory or sessionStorage. */
export function readCachedUserRoles(userId: string | null | undefined): string[] | null {
  if (!userId) return null;
  const host = cacheHost();
  if (
    memoryCachedRoles &&
    memoryCachedRoles.userId === userId &&
    memoryCachedRoles.host === host
  ) {
    return memoryCachedRoles.roles;
  }
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(ROLES_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredUserRoles>;
    if (parsed.userId !== userId || parsed.host !== host || !Array.isArray(parsed.roles)) {
      return null;
    }
    const roles = parsed.roles.filter((role): role is string => typeof role === 'string');
    if (roles.length === 0) return null;
    const normalized = normalizeRoles(roles);
    memoryCachedRoles = { userId, host, roles: normalized };
    return normalized;
  } catch {
    return null;
  }
}

export function storeCachedUserRoles(userId: string, roles: readonly string[]): void {
  if (!userId || roles.length === 0) return;
  const entry: StoredUserRoles = {
    userId,
    host: cacheHost(),
    roles: normalizeRoles(roles),
  };
  memoryCachedRoles = entry;
  try {
    sessionStorage.setItem(ROLES_SESSION_KEY, JSON.stringify(entry));
  } catch {
    // Private mode or quota: the in-memory copy still serves this page.
  }
}

export function clearCachedUserRoles(): void {
  memoryCachedRoles = null;
  try {
    sessionStorage.removeItem(ROLES_SESSION_KEY);
  } catch {
    // ignore
  }
}

function deniedRoles(status: number): Error {
  const error = new Error('Failed to fetch roles from server');
  return Object.assign(error, { deniedRoles: true as const, status });
}

function isDeniedRoles(error: unknown): error is Error & { deniedRoles: true; status: number } {
  return (
    error instanceof Error &&
    'deniedRoles' in error &&
    (error as { deniedRoles?: unknown }).deniedRoles === true
  );
}

let userRolesFetchInflight: Promise<string[]> | null = null;
let navigationConfigFetchInflight: Promise<void> | null = null;

export interface PageRoleConfig {
  requiredRoles: string[];
  pageTitle: string;
  description?: string;
}

export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  type: string;
  icon?: string;
}

export interface NavigationConfig {
  userId: string;
  userRoles: string[];
  navigationItems: NavigationItem[];
}

/** Case-sensitive match for backend `has_privileged_admin_access`. */
export function hasPrivilegedAdminAccess(roles: readonly string[]): boolean {
  return roles.some(
    (role) => role === 'admin' || role === 'System Admin' || role === 'Tenant Admin'
  );
}

/** Privileged admin, or the exact role `manager`. */
export function hasUserManagementAccess(roles: readonly string[]): boolean {
  return hasPrivilegedAdminAccess(roles) || roles.includes('manager');
}

export function administrativeAccessFromRoles(roles: readonly string[]): {
  hasUserManagementAccess: boolean;
  hasAdminPanelAccess: boolean;
  hasAnyAdministrativeAccess: boolean;
} {
  const userManagement = hasUserManagementAccess(roles);
  const adminPanel = hasPrivilegedAdminAccess(roles);
  return {
    hasUserManagementAccess: userManagement,
    hasAdminPanelAccess: adminPanel,
    hasAnyAdministrativeAccess: userManagement,
  };
}

export const useRoles = () => {
  const { isAuthenticated, getAuthHeaders, userInfo, isLoading: authLoading } = useAuth();
  const userId = userInfo?.userId;
  const [userRoles, setUserRoles] = useState<string[]>(() => knownRolesForSession().roles);
  const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [rolesFetched, setRolesFetched] = useState<boolean>(false);
  // True when sessionStorage or the signed-in profile already names this
  // user's roles, so Settings can show Manager/Admin access before the
  // background refresh finishes.
  const [rolesFromCache, setRolesFromCache] = useState<boolean>(
    () => knownRolesForSession().ready
  );
  // True after the transient budget is spent. Stops another fetch on every
  // render without recording the outage as a confirmed non-admin role list.
  const [rolesUnavailable, setRolesUnavailable] = useState<boolean>(false);
  
  // New state for navigation configuration
  const [navigationConfig, setNavigationConfig] = useState<NavigationConfig | null>(null);
  const [isLoadingNavigation, setIsLoadingNavigation] = useState<boolean>(false);
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const [navigationFetched, setNavigationFetched] = useState<boolean>(false);

  // Fetch user roles from the API. Resolves with the fresh roles array so callers
  // can make access decisions on the returned value instead of a (possibly stale)
  // closure over the `userRoles` state.
  const fetchUserRoles = useCallback(async (force = false): Promise<string[]> => {
    // Skip if already fetched and not forcing
    if (rolesFetched && !force) {
      return userRoles;
    }

    if (rolesUnavailable && !force) {
      throw transientUnavailable(null);
    }

    if (!isAuthenticated) {
      setUserRoles(['user']); // Always assume 'user' role even when not authenticated
      setRolesFetched(true);
      return ['user'];
    }

    if (userRolesFetchInflight) {
      // Reuse the in-flight request (including a forced Settings load). Resolving
      // early with the default ['user'] would look like "not admin".
      const roles = await userRolesFetchInflight;
      setUserRoles(roles);
      setRolesFetched(true);
      return roles;
    }

    const execute = async (): Promise<string[]> => {
      const activeUserId = userId ?? readSessionUserId();
      const knownRoles = readCachedUserRoles(activeUserId) ?? readClientRoleHint(activeUserId);
      // A background refresh must not flip Settings back to "Loading..." when
      // this session already knows the user's roles.
      if (knownRoles === null) {
        setIsLoadingRoles(true);
      }
      setRolesError(null);
      setRolesUnavailable(false);

      try {
        const rolesWithDefault = await withTransientRetries(async () => {
          const headers = getAuthHeaders();
          let response: Response;
          try {
            // One attempt per loop. fetchWithAuth's own short retry finishes
            // before a cargo-watch restart is back, so the budget lives here.
            // 12s covers a live remote handshake; ECONNREFUSED still fails fast.
            response = await fetchWithAuth(`${API_BASE_URL}/api/getUserRoles`, {
              method: 'GET',
              headers,
            }, { networkRetries: 0, timeoutMs: FETCH_USER_ROLES_TIMEOUT_MS });
          } catch (error) {
            if (isTransientNetworkError(error)) {
              throw transientUnavailable(0);
            }
            throw error;
          }

          if (response.ok) {
            const data = await response.json();
            const roles: string[] = Array.isArray(data.roles) ? data.roles : [];
            const loaded = roles.includes('user') ? roles : [...roles, 'user'];
            return loaded;
          }

          // 5xx during pool warmup is an outage, not "this user has no roles".
          if (isTransientHttpStatus(response.status) || response.status >= 500) {
            throw transientUnavailable(response.status);
          }

          console.warn('Failed to fetch user roles, using default');
          throw deniedRoles(response.status);
        }, isTransientNetworkError, { attempts: 1 });

        if (activeUserId) {
          storeCachedUserRoles(activeUserId, rolesWithDefault);
        }
        setUserRoles(rolesWithDefault);
        setRolesFromCache(true);
        setRolesFetched(true);
        return rolesWithDefault;
      } catch (error) {
        if (isDeniedRoles(error)) {
          // 401/403 (and other definitive HTTP errors) are not an outage.
          const denied = ['user'];
          if (activeUserId) {
            storeCachedUserRoles(activeUserId, denied);
          }
          setUserRoles(denied);
          setRolesFromCache(false);
          setRolesError('Failed to fetch roles from server');
          setRolesFetched(true);
          return denied;
        }
        if (isTransientNetworkError(error)) {
          // Leave userRoles untouched so a cached admin list is not replaced
          // by the default ['user'] while the API process is restarting.
          console.error('Error fetching user roles:', error);
          setRolesError('Error connecting to server');
          setRolesUnavailable(true);
          throw error;
        }
        console.error('Error fetching user roles:', error);
        if (knownRoles !== null) {
          setRolesError('Error connecting to server');
          setRolesUnavailable(true);
          throw error instanceof Error ? error : transientUnavailable(null);
        }
        setUserRoles(['user']);
        setRolesError('Error connecting to server');
        setRolesFetched(true);
        return ['user'];
      } finally {
        setIsLoadingRoles(false);
      }
    };

    const promise = execute().finally(() => {
      userRolesFetchInflight = null;
    });
    userRolesFetchInflight = promise;
    return await promise;
  }, [isAuthenticated, getAuthHeaders, rolesFetched, rolesUnavailable, userRoles, userId]);

  // Fetch navigation configuration from server (secure, server-side role checking)
  const fetchNavigationConfig = useCallback(async (force = false) => {
    // Skip if already fetched and not forcing
    if (navigationFetched && !force) {
      return;
    }

    // Skip if already loading
    if (isLoadingNavigation) {
      return;
    }

    if (!isAuthenticated) {
      setNavigationConfig(null);
      setNavigationFetched(true);
      return;
    }

    if (!force && navigationConfigFetchInflight) {
      await navigationConfigFetchInflight;
      return;
    }

    const execute = async () => {
      setIsLoadingNavigation(true);
      setNavigationError(null);

      try {
        const headers = getAuthHeaders();
        const response = await fetchWithAuth(`${API_BASE_URL}/api/getNavigationConfig`, {
          method: 'GET',
          headers,
        });

        if (response.ok) {
          const data = await response.json();
          setNavigationConfig(data);
          setNavigationFetched(true);
        } else {
          console.warn('Failed to fetch navigation configuration');
          setNavigationConfig(null);
          setNavigationError('Failed to fetch navigation config from server');
          setNavigationFetched(true);
        }
      } catch (error) {
        console.error('Error fetching navigation configuration:', error);
        setNavigationConfig(null);
        setNavigationError('Error connecting to server');
        setNavigationFetched(true);
      } finally {
        setIsLoadingNavigation(false);
      }
    };

    if (!force) {
      navigationConfigFetchInflight = execute().finally(() => {
        navigationConfigFetchInflight = null;
      });
      await navigationConfigFetchInflight;
    } else {
      await execute();
    }
  }, [isAuthenticated, getAuthHeaders, navigationFetched, isLoadingNavigation]);

  // Fetch roles only when actually needed (for advanced roles)
  const ensureRolesFetched = useCallback(async (requiredRoles: string[]) => {
    // Helper function to determine if we need to fetch roles from API
    const needsAdvancedRoles = (requiredRoles: string[]): boolean => {
      return requiredRoles.some(role => role !== 'user');
    };

    if (!needsAdvancedRoles(requiredRoles)) {
      return; // Skip API call for pages that only need 'user' role
    }
    
    if (isAuthenticated && !rolesFetched && !rolesUnavailable) {
      await fetchUserRoles();
    }
  }, [isAuthenticated, rolesFetched, rolesUnavailable, fetchUserRoles]);

  // Apply session roles as soon as we know who is signed in, including when
  // auth state arrives after the first render.
  useEffect(() => {
    if (!isAuthenticated || rolesFetched) return;
    const id = userId ?? readSessionUserId();
    const known = readCachedUserRoles(id) ?? readClientRoleHint(id);
    if (!known) return;
    setUserRoles(known);
    setRolesFromCache(true);
  }, [isAuthenticated, userId, rolesFetched]);

  // Reset state when user logs out. sessionStorage stays so the same user
  // still has a role list if the API is down on the next visit.
  useEffect(() => {
    if (
      !isAuthenticated &&
      (rolesFetched || rolesUnavailable || rolesFromCache || navigationFetched)
    ) {
      setUserRoles(['user']);
      setRolesFetched(false);
      setRolesFromCache(false);
      setRolesUnavailable(false);
      setRolesError(null);
      setNavigationConfig(null);
      setNavigationFetched(false);
      setNavigationError(null);
    }
  }, [isAuthenticated, rolesFetched, rolesUnavailable, rolesFromCache, navigationFetched]);

  const hasRole = useCallback((role: string): boolean => {
    // Everyone has 'user' role by default
    if (role === 'user') {
      return true;
    }
    return userRoles.includes(role);
  }, [userRoles]);

  const hasAnyRole = useCallback((roles: string[]): boolean => {
    // If 'user' is in the required roles, always return true
    if (roles.includes('user')) {
      return true;
    }
    return roles.some(role => userRoles.includes(role));
  }, [userRoles]);

  const hasAllRoles = useCallback((roles: string[]): boolean => {
    return roles.every(role => hasRole(role));
  }, [hasRole]);

  const checkPageAccess = useCallback((config: PageRoleConfig): boolean => {
    if (!config.requiredRoles || config.requiredRoles.length === 0) {
      return true; // No roles required, everyone has access
    }

    // If page only requires 'user' role and user is authenticated, grant access immediately
    if (config.requiredRoles.length === 1 && config.requiredRoles[0] === 'user') {
      return isAuthenticated; // All authenticated users have 'user' role
    }

    // Trigger role fetching for advanced roles (async, non-blocking)
    ensureRolesFetched(config.requiredRoles).catch(error => {
      console.warn('Failed to fetch advanced roles:', error);
    });

    return hasAnyRole(config.requiredRoles);
  }, [isAuthenticated, ensureRolesFetched, hasAnyRole]);

  const getUserRoles = useCallback((): string[] => {
    return userRoles;
  }, [userRoles]);

  const refreshRoles = useCallback(() => {
    setRolesFetched(false); // Reset fetched state
    fetchUserRoles(true).catch(error => {
      console.warn('Failed to refresh user roles:', error);
    });
  }, [fetchUserRoles]);

  const refreshNavigationConfig = useCallback(() => {
    setNavigationFetched(false); // Reset fetched state
    fetchNavigationConfig(true); // Force fetch
  }, [fetchNavigationConfig]);

  // Get navigation items that user should see (server-determined)
  const getNavigationItems = (): NavigationItem[] => {
    return navigationConfig?.navigationItems || [];
  };

  // Check if navigation item should be shown (server-determined)
  const shouldShowNavigationItem = (itemId: string): boolean => {
    if (!navigationConfig) return false;
    return navigationConfig.navigationItems.some(item => item.id === itemId);
  };

  return {
    hasRole,
    hasAnyRole,
    hasAllRoles,
    checkPageAccess,
    authLoading,
    getUserRoles,
    refreshRoles,
    userRoles,
    isLoadingRoles,
    rolesReady: rolesFetched || rolesFromCache,
    rolesError,
    // New navigation-related functions
    fetchNavigationConfig,
    fetchUserRoles,
    refreshNavigationConfig,
    getNavigationItems,
    shouldShowNavigationItem,
    navigationConfig,
    isLoadingNavigation,
    navigationError
  };
};

 